/**
 * <color-options-swatches>
 *
 * Renders color-variant swatches for sibling products linked via the
 * `custom.color_options` product metafield. On product cards, clicking a
 * swatch swaps the card content in place (image, title, price, link href,
 * active swatch state) without leaving the listing — Adanola-style.
 * The PDP variant of the element is a plain link (no parent product-card).
 */
class ColorOptionsSwatches extends HTMLElement {
  connectedCallback() {
    this.productCard = this.closest('product-card');
    this.swatches = Array.from(this.querySelectorAll('.color-options__swatch'));
    this.selectedSwatch = this.swatches.find((s) => s.classList.contains('is-active')) || null;

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', this._onClick);
    });
  }

  disconnectedCallback() {
    this.swatches?.forEach((swatch) => {
      swatch.removeEventListener('click', this._onClick);
    });
  }

  _onClick = (event) => {
    if (!this.productCard) return; // PDP: let the link navigate.

    // Modifier-click → open in new tab/window via native behavior.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();
    event.stopPropagation();

    const swatch = event.currentTarget;
    if (swatch === this.selectedSwatch) return;

    this._applyFullState(swatch);
    this.selectedSwatch = swatch;
  };

  _applyFullState(swatch) {
    if (!this.productCard) return;

    // Active swatch styling.
    this.swatches.forEach((s) => {
      const active = s === swatch;
      s.classList.toggle('is-active', active);
      if (active) s.setAttribute('aria-current', 'true');
      else s.removeAttribute('aria-current');
    });

    // Image.
    const image = this._getActiveImage();
    if (image) {
      const src = swatch.dataset.imageSrc;
      const srcset = swatch.dataset.imageSrcset;
      if (src) image.setAttribute('src', src);
      if (srcset) image.setAttribute('srcset', srcset);
    }

    // Title.
    const newTitle = swatch.dataset.productTitle;
    if (newTitle) {
      const titleNodes = this.productCard.querySelectorAll('product-title, [ref="productTitleLink"]');
      titleNodes.forEach((node) => {
        const heading = node.querySelector('[role="heading"]') || node.querySelector('p, h1, h2, h3, h4, h5, h6');
        if (heading) heading.textContent = newTitle;
        else if (!node.querySelector('product-title')) node.textContent = newTitle;
      });
    }

    // Price.
    const priceHtml = swatch.dataset.priceHtml;
    if (priceHtml) {
      const priceContainer = this.productCard.querySelector('product-price [ref="priceContainer"]');
      if (priceContainer) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = priceHtml;
        const newContainer = wrapper.querySelector('[ref="priceContainer"]');
        priceContainer.innerHTML = newContainer ? newContainer.innerHTML : priceHtml;
      }
    }

    // Link hrefs (main card link + gallery link + title link).
    const newUrl = swatch.dataset.productUrl;
    if (newUrl) {
      const links = this.productCard.querySelectorAll(
        '[ref="productCardLink"], [ref="cardGalleryLink"], [ref="productTitleLink"]'
      );
      links.forEach((link) => {
        if (link instanceof HTMLAnchorElement) link.href = newUrl;
      });
    }

    // Update product id so quick-add etc. point at the new product.
    const newId = swatch.dataset.productId;
    if (newId) {
      this.productCard.setAttribute('data-product-id', newId);
    }
  }

  _getActiveImage() {
    if (!this.productCard) return null;
    const activeSlide = this.productCard.querySelector('slideshow-slide[aria-hidden="false"]');
    const slide = activeSlide || this.productCard.querySelector('slideshow-slide');
    return slide ? slide.querySelector('img') : null;
  }
}

if (!customElements.get('color-options-swatches')) {
  customElements.define('color-options-swatches', ColorOptionsSwatches);
}
